import React from "react";
import { Box, Card, CardContent, Typography, Skeleton } from "@mui/material";
import {
  NotificationsActiveOutlined,
  VisibilityOutlined,
  CheckCircleOutlined,
} from "@mui/icons-material";
import { MentionStats } from "../types";
import PlatformIcon from "./PlatformIcon";

interface StatsOverviewProps {
  stats: MentionStats | null;
  loading?: boolean;
}

const StatsOverview: React.FC<StatsOverviewProps> = ({ stats, loading }) => {
  const statItems = [
    {
      label: "New Mentions",
      value: stats?.new ?? 0,
      icon: <NotificationsActiveOutlined color="primary" />,
      color: "primary.main",
    },
    {
      label: "Seen",
      value: stats?.seen ?? 0,
      icon: <VisibilityOutlined sx={{ color: "text.secondary" }} />,
      color: "text.secondary",
    },
    {
      label: "Dismissed",
      value: stats?.dismissed ?? 0,
      icon: <CheckCircleOutlined sx={{ color: "text.disabled" }} />,
      color: "text.disabled",
    },
    {
      label: "Reddit",
      value: stats?.byPlatform?.reddit ?? 0,
      icon: <PlatformIcon platform="reddit" size={20} />,
      color: "#FF4500",
    },
    {
      label: "Hacker News",
      value: stats?.byPlatform?.hackernews ?? 0,
      icon: <PlatformIcon platform="hackernews" size={20} />,
      color: "#FF6600",
    },
    {
      label: "LinkedIn",
      value: stats?.byPlatform?.linkedin ?? 0,
      icon: <PlatformIcon platform="linkedin" size={20} />,
      color: "#0A66C2",
    },
    {
      label: "X",
      value: stats?.byPlatform?.x ?? 0,
      icon: <PlatformIcon platform="x" size={20} />,
      color: "#000000",
    },
    {
      label: "Quora",
      value: stats?.byPlatform?.quora ?? 0,
      icon: <PlatformIcon platform="quora" size={20} />,
      color: "#B92B27",
    },
    {
      label: "ChatGPT",
      value: stats?.byPlatform?.chatgpt ?? 0,
      icon: <PlatformIcon platform="chatgpt" size={20} />,
      color: "#10A37F",
    },
    {
      label: "Google AI",
      value: stats?.byPlatform?.google_ai ?? 0,
      icon: <PlatformIcon platform="google_ai" size={20} />,
      color: "#4285F4",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        mb: 3,
      }}
    >
      {statItems.map((item) => (
        <Card
          key={item.label}
          sx={{
            flex: "1 1 150px",
            minWidth: 150,
            maxWidth: 200,
          }}
        >
          <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
              }}
            >
              {item.icon}
              <Typography variant="body2" color="text.secondary">
                {item.label}
              </Typography>
            </Box>
            {loading ? (
              <Skeleton width={60} height={32} />
            ) : (
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {item.value}
              </Typography>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default StatsOverview;
